import { apiClient } from "../client";
/** Extract user-friendly error message from createThread API errors (400/403). */
export function parseCreateThreadError(error) {
    const err = error;
    const status = err?.response?.status;
    const apiMessage = err?.response?.data?.message;
    if (status === 403) {
        return { status: 403, message: "You don't have permission to create this conversation." };
    }
    if (status === 400) {
        return { status: 400, message: apiMessage || "Invalid request. Please check the form." };
    }
    return { status, message: "Failed to create conversation." };
}
const ENDPOINTS = {
    THREADS: "/messages/threads",
    MESSAGES: "/messages",
    SEND: "/messages/send",
    MARK_READ: "/messages/mark-read",
    UNREAD: "/messages/unread",
    STAFF_HOME_LATEST: "/messages/staff/home/latest",
};
export const messagesService = {
    /**
     * Get all message threads for the current user (legacy - uses mock data for now)
     * @deprecated Use getThreadsByContact instead
     */
    getThreads: (_type) => {
        // Return empty array for now - this is the legacy endpoint
        // In production, this should call getThreadsByContact with the current user's contactId
        return Promise.resolve({
            data: [],
            status: 200,
            statusText: "OK",
            headers: {},
            config: {},
        });
    },
    /**
     * Get all message threads for a contact
     *
     * Endpoint: GET /api/messages/threads/contact/{contactId}
     * Query parameters: type (general|job|quote), jobId, quoteId
     *
     * Example: GET /api/messages/threads/contact/{contactId}?type=general
     *
     * Returns empty array when contact has no threads (404/204 or response body { data: [] }).
     */
    getThreadsByContact: async (contactId, filters) => {
        const params = {};
        if (filters?.type) {
            params.type = filters.type;
        }
        if (filters?.jobId) {
            params.jobId = filters.jobId;
        }
        if (filters?.quoteId) {
            params.quoteId = filters.quoteId;
        }
        try {
            return await apiClient.get(`${ENDPOINTS.THREADS}/contact/${contactId}`, { params });
        }
        catch (error) {
            const err = error;
            const status = err?.response?.status;
            const body = err?.response?.data;
            const isEmptyData = body &&
                typeof body === "object" &&
                "data" in body &&
                Array.isArray(body.data) &&
                body.data.length === 0;
            if (status === 404 ||
                status === 204 ||
                isEmptyData) {
                return { data: { data: [] } };
            }
            throw error;
        }
    },
    /**
     * Get a single thread with messages
     */
    getThread: (threadId) => apiClient.get(`${ENDPOINTS.THREADS}/${threadId}`),
    /**
     * Get the single quote thread for a quote and assigned contact, with messages.
     * Thread is identified by (quoteId, assignedContactId). contactId in the API is the
     * assigned contact for that quote conversation. Returns null when no thread exists yet (404).
     * GET /api/messages/threads/by-quote?quoteId=&contactId=
     */
    getThreadByQuote: async (quoteId, assignedContactId) => {
        try {
            const response = await apiClient.get(`${ENDPOINTS.THREADS}/by-quote`, {
                params: { quoteId, contactId: assignedContactId },
            });
            const raw = response.data;
            const data = raw && typeof raw === "object" && "thread" in raw && "messages" in raw
                ? raw
                : raw?.data;
            if (data?.thread && Array.isArray(data.messages)) {
                return data;
            }
            return null;
        }
        catch (error) {
            const status = error?.response
                ?.status;
            if (status === 404) {
                return null;
            }
            throw error;
        }
    },
    /**
     * Get the single job thread for a job and assigned contact, with messages.
     * Thread is identified by (jobId, assignedContactId). contactId in the API is the
     * assigned contact for that job conversation. Returns null when no thread exists yet (404).
     * GET /api/messages/threads/by-job?jobId=&contactId=
     */
    getThreadByJob: async (jobId, assignedContactId) => {
        try {
            const response = await apiClient.get(`${ENDPOINTS.THREADS}/by-job`, {
                params: { jobId, contactId: assignedContactId },
            });
            const raw = response.data;
            const data = raw && typeof raw === "object" && "thread" in raw && "messages" in raw
                ? raw
                : raw?.data;
            if (data?.thread && Array.isArray(data.messages)) {
                return data;
            }
            return null;
        }
        catch (error) {
            const status = error?.response
                ?.status;
            if (status === 404) {
                return null;
            }
            throw error;
        }
    },
    /**
     * Get all general threads for the admin UI (staff). No contactId.
     * Returns array of thread summaries with id, contactId, lastMessageAt, etc.
     * GET /api/messages/threads/by-general
     */
    getGeneralThreads: async () => {
        const response = await apiClient.get(`${ENDPOINTS.THREADS}/by-general`);
        const raw = response.data;
        if (Array.isArray(raw)) {
            return raw;
        }
        const arr = raw?.data;
        return Array.isArray(arr) ? arr : [];
    },
    /**
     * Staff-only list of quote threads.
     * GET /api/messages/threads/quote
     * Returns 403 for contacts; returns [] on 403.
     */
    getQuoteThreads: async () => {
        try {
            const response = await apiClient.get(`${ENDPOINTS.THREADS}/quote`);
            return response.data?.data ?? [];
        }
        catch (error) {
            const status = error?.response
                ?.status;
            if (status === 403)
                return [];
            throw error;
        }
    },
    /**
     * Staff-only list of job threads.
     * GET /api/messages/threads/job
     * Returns 403 for contacts; returns [] on 403.
     */
    getJobThreads: async () => {
        try {
            const response = await apiClient.get(`${ENDPOINTS.THREADS}/job`);
            return response.data?.data ?? [];
        }
        catch (error) {
            const status = error?.response
                ?.status;
            if (status === 403)
                return [];
            throw error;
        }
    },
    /**
     * Get the single general thread for a contact, with messages.
     * Returns null when no general thread exists yet (404).
     * GET /api/messages/threads/by-general?contactId=
     */
    getThreadByGeneral: async (contactId) => {
        try {
            const response = await apiClient.get(`${ENDPOINTS.THREADS}/by-general`, {
                params: { contactId },
            });
            const raw = response.data;
            const data = raw && typeof raw === "object" && "thread" in raw && "messages" in raw
                ? raw
                : raw?.data;
            if (data?.thread && Array.isArray(data.messages)) {
                return data;
            }
            return null;
        }
        catch (error) {
            const status = error?.response
                ?.status;
            if (status === 404) {
                return null;
            }
            throw error;
        }
    },
    /**
     * Create a new thread
     */
    createThread: (data) => apiClient.post(ENDPOINTS.THREADS, data),
    /**
     * Get messages for a specific thread
     */
    getMessages: (threadId) => apiClient.get(`${ENDPOINTS.MESSAGES}/${threadId}`),
    /**
     * Send a new message
     */
    sendMessage: (data) => {
        // Build request body matching API format (assignedcontactId, senderId, senderName for thread/sender)
        const requestBody = {
            content: data.content,
        };
        if (data.threadId) {
            requestBody.threadId = data.threadId;
        }
        if (data.parentId) {
            requestBody.parentId = data.parentId;
        }
        if (data.threadType) {
            requestBody.threadType = data.threadType;
        }
        else if (data.type) {
            requestBody.threadType = data.type === "contact" ? "general" : data.type;
        }
        if (data.assignedcontactId) {
            requestBody.assignedcontactId = data.assignedcontactId;
        }
        if (data.contactId) {
            requestBody.contactId = data.contactId;
        }
        if (data.senderId) {
            requestBody.senderId = data.senderId;
        }
        if (data.senderName) {
            requestBody.senderName = data.senderName;
        }
        if (data.jobId) {
            requestBody.jobId = data.jobId;
        }
        else if (data.type === "job" && data.relatedEntityId) {
            requestBody.jobId = data.relatedEntityId;
        }
        if (data.quoteId) {
            requestBody.quoteId = data.quoteId;
        }
        else if (data.type === "quote" && data.relatedEntityId) {
            requestBody.quoteId = data.relatedEntityId;
        }
        return apiClient.post(ENDPOINTS.SEND, requestBody);
    },
    /**
     * Mark messages in a thread as read
     */
    markAsRead: (threadId) => apiClient.patch(`${ENDPOINTS.MARK_READ}/${threadId}`),
    /**
     * Get unread message count for a contact
     */
    getUnreadCount: (contactId) => apiClient.get(`${ENDPOINTS.UNREAD}/${contactId}`),
    /**
     * Get latest message threads for staff home page (last N days).
     * Staff-only: contacts receive 403; returns empty data.
     * GET /api/messages/staff/home/latest?days=3
     */
    getStaffHomeLatestMessages: async (days = 3) => {
        const empty = {
            general: [],
            quote: [],
            job: [],
        };
        try {
            const response = await apiClient.get(ENDPOINTS.STAFF_HOME_LATEST, { params: { days } });
            const data = response.data;
            return {
                general: data?.general ?? [],
                quote: data?.quote ?? [],
                job: data?.job ?? [],
            };
        }
        catch (error) {
            const status = error?.response
                ?.status;
            if (status === 403) {
                return empty;
            }
            throw error;
        }
    },
};
